import { UserProfileResponse } from '@core/data-access/models/api/user-profile.model';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { VoucherWelcomePreviewResponse } from '@core/data-access/models/exhibitor/exhibitor-api.model';
import { ExhibitorDetail } from '@core/data-access/models/exhibitor/exhibitor.model';
import { CheckedTariffEmailAddressValidity } from '@core/data-access/models/form/form-validity.model';
import { toUTCDateString } from '@core/utils/datetime-helpers';
import { ValidateDailyTicketPerEmailLimitResult } from '@products/models/tariff-status.model';
import { TariffUuid, TicketPersonId } from '@products/models/tariff.model';
import { forEach, head, isNil, omit, some } from 'lodash';

export const mapVoucherWelcomePreviewResponseToExhibitorDetail = (
  voucherWelcomePreviewResponse: VoucherWelcomePreviewResponse | null
): ExhibitorDetail | null => {
  if (isNil(voucherWelcomePreviewResponse)) {
    return voucherWelcomePreviewResponse;
  }

  const {
    companyTriple,
    hallId,
    standId,
    floorId,
    welcomeMessageBackgroundColor,
    banner,
    bannerLink,
    logo,
    logoLink,
    voucherWelcomeMessages
  } = voucherWelcomePreviewResponse;

  return {
    companyTriple,
    hallId,
    standId,
    floorId,
    welcomeMessageBackgroundColor,
    banner,
    bannerLink,
    logo,
    logoLink,
    welcomeMessages: voucherWelcomeMessages
  };
};

export const mapValidatedDailyTicketPerLimitResponseToCheckedValidity = (
  ticketLimitPerEmail: number,
  emailAddressesWithTicketPersonIds: Record<TariffUuid, { email: string; ticketPersonId: TicketPersonId }>,
  validateDailyTicketPerEmailLimitResult: ValidateDailyTicketPerEmailLimitResult
): CheckedTariffEmailAddressValidity => {
  const { isValid, errors } = validateDailyTicketPerEmailLimitResult;

  const checkedTariffEmailAddressValidity: CheckedTariffEmailAddressValidity = {};

  if (isValid) {
    return checkedTariffEmailAddressValidity;
  }

  forEach(emailAddressesWithTicketPersonIds, ({ email }, tariffUuid) => {
    const isCurrentEmailInvalid = some(errors, (error) => head(error.values) === email);

    if (isCurrentEmailInvalid) {
      checkedTariffEmailAddressValidity[tariffUuid] = {
        label: '',
        value: email,
        errors: {
          ticketLimitPerEmailAddress: ticketLimitPerEmail
        }
      };
    }
  });

  return checkedTariffEmailAddressValidity;
};

export const mapGermanBuyerInformationUrlParamsToFormFieldNames = (): Record<string, FieldKey> => ({
  besucher_firma: FieldKey.Company,
  besucher_br_anrede_id: FieldKey.Gender,
  besucher_titel: FieldKey.Title,
  besucher_vorname: FieldKey.LastName,
  besucher_name: FieldKey.FirstName,
  besucher_funktion: FieldKey.Function,
  besucher_strasse: FieldKey.Street,
  besucher_plz: FieldKey.ZipCode,
  besucher_ort: FieldKey.City,
  besucher_country: FieldKey.Country,
  besucher_email: FieldKey.Email,
  besucher_telefon: FieldKey.Telephone
});

export const mapQueryParamGenderKeyValueToFormFieldValue = (queryParamKeyValue: string) => {
  switch (queryParamKeyValue) {
    case '0':
      return 'gender.male';
    case '1':
      return 'gender.female';
    case '2':
      return 'gender.diverse';
    case '3':
      return 'gender.notSpecified';
  }

  return null;
};

export const mapUserProfileResponseToUserProfile = (userProfileResponse: UserProfileResponse) => {
  const updatedUserProfileResponse = omit(userProfileResponse, 'languageCode');
  const { dateOfBirth, gender } = updatedUserProfileResponse;

  return {
    ...updatedUserProfileResponse,
    dateOfBirth: !isNil(dateOfBirth) ? toUTCDateString(new Date(dateOfBirth)) : null,
    gender: gender !== 'notSet' ? gender : null
  };
};
